import React, { useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import settings from '@/settings';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import i18n from '@/settings/i18n';
import { hasAnalyticsConsent } from '@/lib/utils/cookie-consent';
import {
  TRUSTPILOT_REVIEW_LANGUAGE,
  TRUSTPILOT_REVIEW_URL,
  TRUSTPILOT_WIDGET_LOCALE,
} from '@/lib/trustpilot';

const t = i18n.init('shared');

export default function Reviews() {
  const trustBoxRef = useRef<HTMLDivElement>(null);
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    setConsentGiven(hasAnalyticsConsent());
  }, []);

  useEffect(() => {
    // Handles the case where the Trustpilot script was already loaded (e.g. by
    // CookieConsentScripts) before consentGiven flipped to true.
    if (consentGiven && (window as any).Trustpilot) {
      (window as any).Trustpilot.loadFromElement(trustBoxRef.current, true);
    }
  }, [consentGiven]);

  return (
    <section className="bg-white">
      {consentGiven && (
        <Script
          src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
          strategy="afterInteractive"
          onLoad={() => {
            (window as any).Trustpilot?.loadFromElement(
              trustBoxRef.current,
              true,
            );
          }}
        />
      )}
      <WebsiteSchema
        title={t`Reebelo Trustpilot Reviews`}
        description={t`Reebelo ${{
          country: settings.country,
        }} is APAC's leading marketplace for sustainable tech with offices in ${{
          offices: settings.country_office_locations,
        }}. The positive customer experience at Reebelo ${{
          country: settings.country,
        }} is of utmost importance to us. Please check here for Reebelo ${{
          country: settings.country,
        }} Trustpilot Reviews.`}
      />

      <div className="reebelo-container flex max-w-[800px] flex-wrap rounded py-8 md:py-10">
        <div className="mb-4 w-full">
          <h1 className="mb-3 mt-0 text-center text-2xl font-semibold text-gray-700 md:mb-8 md:mt-2 md:text-3xl">
            {t`Reebelo Trustpilot Reviews`}
          </h1>
          <p className="mt-10 text-sm leading-relaxed text-gray-700 md:text-base">
            {t`As a trusted marketplace in ${{
              country: settings.country,
            }} for refurbished tech, Reebelo places a lot of importance on its customers. We strive to bring them the best value for money and continue to adhere to stringent values ourselves - our promises of quality. It’s what makes Reebelo a reliable participant in the circular economy - connecting vetted vendors with customers looking for great tech.`}
          </p>
          <p className="mt-5 text-sm leading-relaxed text-gray-700 md:text-base">
            {t`We serve 500,000 customers globally and humbly sport an ‘excellent’ rating on Trustpilot. Like every great company, we will continue to strive to keep you happy.`}
          </p>
          <p className="mb-4 mt-5 text-sm leading-relaxed text-gray-700 md:text-base">
            {t`See what our customers are saying:`}
          </p>
          <div
            ref={trustBoxRef}
            className="trustpilot-widget"
            data-locale={TRUSTPILOT_WIDGET_LOCALE}
            data-template-id="539ad60defb9600b94d7df2c"
            data-businessunit-id="5da6e25d045cf9000135f914"
            data-style-height="500px"
            data-style-width="100%"
            data-theme="light"
            data-stars="4,5"
            data-review-languages={TRUSTPILOT_REVIEW_LANGUAGE}
            data-text-color="#1f2323"
            data-font-family="Manrope, sans-serif"
          >
            <a
              href={TRUSTPILOT_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Trustpilot
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
